import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { faDownload, faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';

import { call } from '@spacefill/shared/src/utils/Backend';
import { formatDateTime } from '@spacefill/shared/src/utils/Date';
import { useAnnouncementsContext } from '@spacefill/shared/src/utils/contexts/AnnouncementsContext';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { Box } from '@spacefill/uikit/src/components/Box/Box';
import { Button } from '@spacefill/uikit/src/components/Button';
import { ButtonText } from '@spacefill/uikit/src/components/ButtonText/ButtonText';
import { Chip } from '@spacefill/uikit/src/components/Chip/Chip';
import { EmptyState } from '@spacefill/uikit/src/components/EmptyState';
import { IconButton } from '@spacefill/uikit/src/components/IconButton';
import { Loader } from '@spacefill/uikit/src/components/Loader';
import { Popover } from '@spacefill/uikit/src/components/Popover';
import { SidePanel } from '@spacefill/uikit/src/components/SidePanel';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import { CreateAnnouncementModal } from './CreateAnnouncementModal';

export function AnnouncementsSidePanel() {
    const { t } = useTranslation();
    const { isSidePanelOpen, closeSidePanel, creationEnabled } = useAnnouncementsContext();
    const queryClient = useQueryClient();
    const { user } = useUserContext();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['/in-app-notifications/list-announcements'],
        queryFn: () => call('/in-app-notifications/list-announcements'),
        enabled: isSidePanelOpen,
    });

    const announcements = data?.data || [];

    const handleCreateSuccess = async () => {
        setIsCreateModalOpen(false);
    };

    const deleteMutation = useMutation({
        mutationFn: (id) => {
            return call('/in-app-notifications/unpublish-announcement', { id }, 'POST');
        },
        async onSuccess() {
            await queryClient.invalidateQueries({ queryKey: ['/in-app-notifications/list-announcements'] });
            toast.success(t('Announcement has been unpublished.'));
        },
        onError(error) {
            console.error('error unpublishing announcement: ', error);
            toast.error(t('Internal error!'));
        },
    });

    const readMutation = useMutation({
        mutationFn: (id) => call(`/in-app-notifications/${id}/read`, {}, 'POST'),
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: ['/in-app-notifications/list-announcements'],
            });
            await queryClient.invalidateQueries({
                queryKey: ['/in-app-notifications/count/unread'],
            });
        },
        onError(error) {
            console.error('error during read mutation: ', error);
            toast.error(t('Internal error!'));
        },
    });

    return (
        <>
            <SidePanel
                isOpen={isSidePanelOpen}
                onClose={closeSidePanel}
                width={800}
                title={
                    <>
                        {t('Announcements')}
                        <p className='text-gray-500 text-sm font-normal'>
                            {t('Centralise the announcements exchanged with your partners.')}
                        </p>
                    </>
                }
                titleSideElement={
                    !user.isRestrictedRole &&
                    creationEnabled && (
                        <Button
                            onClick={() => setIsCreateModalOpen(true)}
                            iconStart={<FontAwesomeIcon icon={faPlus} />}
                        >
                            {t('New announcement')}
                        </Button>
                    )
                }
            >
                {isLoading ? (
                    <Loader />
                ) : announcements.length === 0 ? (
                    <EmptyState paragraph={t('No announcements at the moment. New announcements will appear here.')} />
                ) : (
                    <div className='bg-gray-100 h-full -ml-6 -mr-6 -mb-6 p-6 flex flex-col gap-4'>
                        {announcements.map((announcement) => (
                            <AnnouncementItem
                                key={announcement.id}
                                announcement={announcement}
                                deleteMutation={deleteMutation}
                                readMutation={readMutation}
                            />
                        ))}
                    </div>
                )}
            </SidePanel>

            {isCreateModalOpen && (
                <CreateAnnouncementModal
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSuccess={handleCreateSuccess}
                />
            )}
        </>
    );
}

function AnnouncementItem({ announcement, readMutation, deleteMutation }) {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const {
        title = '',
        content = '',
        creatorName = '',
        createdAt,
        attachments,
        recipientIdsByType,
    } = announcement.templateParams.announcement || {};
    const isOwner = announcement.createdById === user.id;
    const recipientLabels = getRecipientLabels(recipientIdsByType, user);

    return (
        <Box
            className={clsx('py-3', announcement.readAt ? 'bg-white' : 'bg-blue-50')}
            bordered
        >
            <div className='relative flex items-center justify-between gap-2'>
                <button
                    onClick={() => readMutation.mutate(announcement.id)}
                    className={clsx('cursor-pointer text-left', announcement.readAt ? 'opacity-50' : 'opacity-100')}
                >
                    <p className='flex items-center font-semibold text-gray-900'>
                        {title}
                        {!announcement.readAt && (
                            <Chip
                                variant='normal'
                                color='blue'
                                className='ml-2'
                            >
                                {t('Unread')}
                            </Chip>
                        )}
                    </p>
                    <span className='text-xs text-gray-400 shrink-0'>
                        {t('By {{person}}', { person: creatorName })} {formatDateTime(createdAt)}
                    </span>
                </button>
                {isOwner && (
                    <Popover
                        position='left'
                        strategy='absolute'
                        inPortal={false}
                        content={() => (
                            <div className='m-2'>
                                <p>{t('Are you sure you want to delete this?')}</p>
                                <div className='mt-4 flex justify-between'>
                                    <Button variant='secondary'>{t('No')}</Button>
                                    <Button
                                        variant='danger'
                                        onClick={async () => {
                                            deleteMutation.mutate(announcement.id);
                                        }}
                                    >
                                        {t('Yes')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    >
                        <IconButton
                            variant='outlined'
                            icon={faTrashAlt}
                            label={t('Unpublish announcement')}
                            disabled={deleteMutation.isPending}
                            className='ml-auto'
                        />
                    </Popover>
                )}
            </div>
            <button
                onClick={() => {
                    readMutation.mutate(announcement.id);
                }}
                className={clsx(
                    'cursor-pointer text-left mt-1 text-sm text-gray-600',
                    announcement.readAt ? 'opacity-50' : 'opacity-100'
                )}
            >
                {content}
            </button>
            {isOwner && recipientLabels.length > 0 && (
                <p className='mt-2 text-xs text-gray-500 pt-2 border-t border-solid border-gray-300'>
                    {t('Recipients')}: {recipientLabels.join(', ')}
                </p>
            )}

            {attachments?.length > 0 && (
                <div
                    className='mt-2'
                    onClick={() => {
                        readMutation.mutate(announcement.id);
                    }}
                >
                    {attachments.map((file) => {
                        const filenameFull = file.split('/').pop();
                        const filename = filenameFull.split('?')[0];

                        return (
                            <ButtonText
                                key={file}
                                href={file}
                                type='external-link'
                                htmlAttributes={{
                                    target: '_blank',
                                    rel: 'noopener noreferrer',
                                }}
                                className={clsx('inline-flex', announcement.readAt ? 'opacity-50' : 'opacity-100')}
                            >
                                <FontAwesomeIcon icon={faDownload} /> {t('Download')} {filename}
                            </ButtonText>
                        );
                    })}
                </div>
            )}
        </Box>
    );
}

function getRecipientLabels(recipientIdsByType, user) {
    if (!recipientIdsByType) {
        return [];
    }

    const labels = [];

    const warehousesById = new Map((user.warehouses ?? []).map((warehouse) => [warehouse.id, warehouse.label]));
    const customersById = new Map((user.customers ?? []).map((customer) => [customer.id, customer.company]));
    const logisticProvidersById = new Map(
        (user.logisticProviders ?? []).map((provider) => [provider.id, provider.company])
    );
    const customerCarriersById = new Map(
        (user.customerCarriers ?? []).map((customerCarrier) => [
            customerCarrier.id,
            customerCarrier?.carrier?.company || customerCarrier?.customer?.company,
        ])
    );

    for (const warehouseId of recipientIdsByType.logisticProviderWarehouseIds ?? []) {
        const label = warehousesById.get(warehouseId);
        if (label) {
            labels.push(label);
        }
    }

    for (const customerId of recipientIdsByType.customerIds ?? []) {
        const label = customersById.get(customerId);
        if (label) {
            labels.push(label);
        }
    }

    for (const logisticProviderId of recipientIdsByType.logisticProviderIds ?? []) {
        const label = logisticProvidersById.get(logisticProviderId);
        if (label) {
            labels.push(label);
        }
    }

    for (const customerCarrierId of recipientIdsByType.customerCarrierIds ?? []) {
        const label = customerCarriersById.get(customerCarrierId);
        if (label) {
            labels.push(label);
        }
    }

    return [...new Set(labels)];
}
